import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Card,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useEmployeeUpdateForm } from '../hooks/useEmployeeUpdateForm';
import { updateEmployeeProfile } from '../api/client';
import { UserSelectionStep } from '../components/employeeUpdate/steps/UserSelectionStep';
import { EmployeeDetailsStep } from '../components/employeeUpdate/steps/EmployeeDetailsStep';
import { AccessStep } from '../components/employeeUpdate/steps/AccessStep';
import { SoftwareStep } from '../components/employeeUpdate/steps/SoftwareStep';
import { ReviewStep } from '../components/employeeUpdate/steps/ReviewStep';
import { ROUTES } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: tokens.spacingVerticalXXL,
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  stepper: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXXL,
    overflowX: 'auto',
  },
  step: {
    flex: 1,
    minWidth: '120px',
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    borderBottom: `3px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  stepActive: {
    borderBottomColor: tokens.colorBrandForeground1,
  },
  stepCompleted: {
    borderBottomColor: tokens.colorPaletteGreenForeground1,
  },
  stepNumber: {
    display: 'inline-block',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground2,
    lineHeight: '32px',
    marginBottom: tokens.spacingVerticalS,
  },
  stepNumberActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stepNumberCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  content: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalM,
  },
});

const STEPS = [
  { label: 'Select User', key: 0 },
  { label: 'Employee Details', key: 1 },
  { label: 'Access', key: 2 },
  { label: 'Software', key: 3 },
  { label: 'Review', key: 4 },
];

export function EmployeeUpdatePage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const {
    formData,
    currentStep,
    updateFormData,
    setUserData,
    detectChanges,
    validateStep,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
  } = useEmployeeUpdateForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [requestId, setRequestId] = useState<string>('');

  const handleNext = () => {
    if (validateStep(currentStep)) {
      nextStep();
      setSubmitError(null);
    } else {
      setSubmitError('Please complete all required fields before continuing.');
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      setSubmitError('Please make at least one change before submitting.');
      return;
    }

    const { changes, groupsToAdd, groupsToRemove } = detectChanges();

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await updateEmployeeProfile({
        userId: formData.selectedUserId,
        userDisplayName: formData.selectedUserName,
        userPrincipalName: formData.originalUserData!.userPrincipalName,
        changes,
        groupsToAdd,
        groupsToRemove,
        requestedSoftware: formData.requestedSoftware,
        beforeSnapshot: formData.originalUserData! as any,
        afterSnapshot: {
          ...formData.originalUserData!,
          ...changes,
          groups: formData.selectedGroups,
        } as any,
      });

      setRequestId(result.requestId);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to update employee profile'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    resetForm();
    navigate(ROUTES.HISTORY);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <UserSelectionStep formData={formData} onUserSelect={setUserData} />;
      case 1:
        return <EmployeeDetailsStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <AccessStep formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <SoftwareStep formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <ReviewStep formData={formData} detectedChanges={detectChanges()} />;
      default:
        return null;
    }
  };

  const canGoNext = validateStep(currentStep);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text as="h1" size={900} weight="bold">
          Update Employee Profile
        </Text>
        <Text>Modify employee details, access, and software requests.</Text>
      </div>

      <Card>
        {/* Stepper */}
        <div className={styles.stepper}>
          {STEPS.map((step) => {
            const isActive = currentStep === step.key;
            const isCompleted = currentStep > step.key;
            const canClick = isCompleted || currentStep === step.key;

            return (
              <div
                key={step.key}
                className={`${styles.step} ${isActive ? styles.stepActive : ''} ${isCompleted ? styles.stepCompleted : ''}`}
                onClick={() => canClick && goToStep(step.key)}
                style={{ cursor: canClick ? 'pointer' : 'not-allowed' }}
              >
                <div
                  className={`${styles.stepNumber} ${isActive ? styles.stepNumberActive : ''} ${isCompleted ? styles.stepNumberCompleted : ''}`}
                >
                  {isCompleted ? <Checkmark24Regular /> : step.key + 1}
                </div>
                <Text size={200} weight={isActive ? 'semibold' : 'regular'}>
                  {step.label}
                </Text>
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className={styles.content}>{renderStepContent()}</div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button
            icon={<ArrowLeft24Regular />}
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Back
          </Button>

          <div style={{ flex: 1 }} />

          {currentStep < STEPS.length - 1 ? (
            <Button
              appearance="primary"
              icon={<ArrowRight24Regular />}
              onClick={handleNext}
              disabled={!canGoNext}
            >
              Next
            </Button>
          ) : (
            <Button
              appearance="primary"
              icon={<Checkmark24Regular />}
              onClick={handleSubmit}
              disabled={isSubmitting || !canGoNext}
            >
              {isSubmitting ? <Spinner size="tiny" /> : 'Submit Changes'}
            </Button>
          )}
        </div>

        {submitError && (
          <Text className={styles.error} block>
            {submitError}
          </Text>
        )}
      </Card>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={(_, data) => !data.open && handleSuccessClose()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Profile Updated Successfully</DialogTitle>
            <DialogContent>
              <Text>
                The employee profile has been updated successfully. Request ID: <strong>{requestId}</strong>
              </Text>
              <Text block style={{ marginTop: tokens.spacingVerticalM }}>
                Administrators have been notified of the changes via email.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleSuccessClose}>
                View History
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
